import React, { useEffect, useRef, useState } from 'react';
import type { TimeDisplayFont } from './fontOptions';
import { getFontOption } from './fontOptions';
import SevenSegDigit from './SevenSegDigit';
import SevenSegColon from './SevenSegColon';
import { useSevenSegConfig } from './useSevenSegConfig';

interface TimeDisplayProps {
    isCountdown: boolean;
    currentHour: string;
    currentMinute: string;
    currentSecond: string;
    font?: TimeDisplayFont;
    fontSize?: number; // レスポンシブフォントサイズ
    compact?: boolean;
}

export const TimeDisplay: React.FC<TimeDisplayProps> = ({
    isCountdown,
    currentHour,
    currentMinute,
    currentSecond,
    font = 'normal',
    fontSize: responsiveFontSize,
    compact = false,
}) => {
    const fontOption = getFontOption(font);
    const fontFamily = fontOption.fontFamily;
    const isDigital7 =
        font === 'digital-7' ||
        font === 'digital-7-dark' ||
        font === 'digital-7-max' ||
        font === 'digital-7-max-dark';
    const isMax = font === 'digital-7-max' || font === 'digital-7-max-dark';
    const isDark = fontOption.isDark === true;

    // 7セグメント表示の設定
    const segConfig = useSevenSegConfig(isDark);
    // レスポンシブ対応: fontSizeに基づいてサイズを計算
    // MAX版の場合はサイズを大きくする
    // カウントダウンの場合は2回り小さくする
    const baseFontSize = responsiveFontSize || 16;
    const maxScaleFactor = isMax ? 1.6 : 1;
    const countdownScaleFactor = isCountdown ? 0.88 : 1;
    const scaleFactor = maxScaleFactor * countdownScaleFactor;
    const {
        width: segWidth,
        height: segHeight,
        thickness: segThickness,
        colonWidth,
    } = segConfig.getTimeDisplaySize(baseFontSize * scaleFactor);

    // デジタルMAX用：横幅いっぱいに収まるようにスケール調整
    const containerRef = useRef<HTMLDivElement | null>(null);
    const rowRef = useRef<HTMLDivElement | null>(null);
    const [maxScale, setMaxScale] = useState(1);

    useEffect(() => {
        if (!isDigital7 || !isMax) {
            setMaxScale(1);
            return;
        }

        const container = containerRef.current;
        const row = rowRef.current;
        if (!container || !row) return;

        const calcScale = () => {
            const containerWidth = container.getBoundingClientRect().width;
            const contentWidth = row.scrollWidth;
            if (!containerWidth || !contentWidth) return;
            // 少し余白を残して横幅いっぱいに合わせる
            const scale = (containerWidth * 0.89) / contentWidth;
            // 極端な値は避ける（あまり小さくなりすぎないように下限を少し上げる）
            setMaxScale(Math.max(0.7, Math.min(3, scale)));
        };

        const observer = new ResizeObserver(() => {
            calcScale();
        });

        observer.observe(container);

        // 初回計算
        calcScale();

        return () => {
            observer.disconnect();
        };
        // 秒単位で幅が変わる可能性があるので、時刻が変わるたびに再計算
    }, [isDigital7, isMax, currentHour, currentMinute, currentSecond]);

    // 数字を1文字ずつに分割する関数
    const renderDigits = (value: string, fontSize: string = '1em', scale: number = 1) => {
        if (!isDigital7) {
            // Digital-7以外は従来通り
            return <span style={{ fontSize, ...(fontFamily ? { fontFamily } : {}) }}>{value}</span>;
        }

        // 7セグメント表示
        const scaledWidth = segWidth * scale;
        const scaledHeight = segHeight * scale;
        const scaledThickness = segThickness * scale;

        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35em',
                    height: `${scaledHeight}px`,
                }}
                className="max-w-full"
            >
                {value.split('').map((digit, index) => (
                    <div
                        key={index}
                        style={{
                            width: `${scaledWidth}px`,
                            height: `${scaledHeight}px`,
                            flexShrink: 0,
                        }}
                        className="max-w-full max-h-full"
                    >
                        <SevenSegDigit
                            value={digit}
                            width={scaledWidth}
                            height={scaledHeight}
                            thickness={scaledThickness}
                            on={segConfig.segOnColor}
                            off={segConfig.segOffColor}
                        />
                    </div>
                ))}
            </span>
        );
    };

    // コロンの点滅（秒の偶奇でON/OFF）
    const blink = parseInt(currentSecond, 10) % 2 === 0;

    if (isDigital7) {
        // 7セグメント表示
        return (
            <div
                ref={containerRef}
                className={`flex justify-center select-none ${
                    compact
                        ? 'mt-0 w-auto shrink-0 items-center'
                        : 'w-full lg:w-full mt-1 items-end'
                }`}
            >
                <div
                    ref={rowRef}
                    className={`flex justify-center max-w-full ${
                        isMax ? 'flex-nowrap' : 'flex-wrap'
                    } ${compact ? 'items-center gap-1' : 'items-end gap-6'}`}
                    style={
                        isMax
                            ? {
                                  transform: `scale(${maxScale})`,
                                  transformOrigin: compact ? 'center center' : 'center bottom',
                              }
                            : undefined
                    }
                >
                    {isCountdown && (
                        <div
                            style={{
                                width: `${segWidth}px`,
                                height: `${segHeight}px`,
                                flexShrink: 0,
                            }}
                            className="max-w-full max-h-full"
                        >
                            <SevenSegDigit
                                value="-"
                                width={segWidth}
                                height={segHeight}
                                thickness={segThickness}
                                on={segConfig.segOnColor}
                                off={segConfig.segOffColor}
                            />
                        </div>
                    )}
                    {/* 時 */}
                    {renderDigits(currentHour)}
                    <div
                        style={{
                            width: `${colonWidth}px`,
                            height: `${segHeight}px`,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        className="max-w-full max-h-full"
                    >
                        <SevenSegColon
                            on={blink}
                            width={colonWidth}
                            height={segHeight}
                            onColor={segConfig.colonOnColor}
                            offColor={segConfig.colonOffColor}
                        />
                    </div>
                    {/* 分 */}
                    {renderDigits(currentMinute)}
                    <div
                        style={{
                            width: `${colonWidth}px`,
                            height: `${segHeight}px`,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        className="max-w-full max-h-full"
                    >
                        <SevenSegColon
                            on={blink}
                            width={colonWidth}
                            height={segHeight}
                            onColor={segConfig.colonOnColor}
                            offColor={segConfig.colonOffColor}
                        />
                    </div>
                    {/* 秒（小さめ、下揃え） */}
                    {renderDigits(currentSecond, '0.5em', 0.5)}
                </div>
            </div>
        );
    }

    // 通常表示
    const textColor = isDark ? '#e5e7eb' : '#4b5563';

    return (
        <div
            className={`text-center select-none ${
                compact
                    ? 'mt-0 w-auto shrink-0 flex items-center justify-center'
                    : 'w-full lg:w-full mt-1'
            }`}
        >
            <span
                className={`leading-none ${compact ? 'ml-0' : 'ml-3'}`}
                style={{
                    fontSize: compact ? `${Math.max(responsiveFontSize || 14, 14)}px` : '4em',
                    fontFamily,
                    color: textColor,
                }}
            >
                {isCountdown && (
                    <span style={{ color: isDark && isDigital7 ? '#ffffff' : undefined }}>- </span>
                )}
                {/* 時 */}
                {renderDigits(currentHour)}
                <span
                        style={{
                            fontSize: '0.5em',
                            color: isDark && isDigital7 ? '#ffffff' : isDark ? '#ffffff' : undefined,
                        }}
                    className={isDark ? '' : 'text-gray-500'}
                >
                    ：
                </span>
                {/* 分 */}
                {renderDigits(currentMinute)}
                {/* 秒 */}
                <span
                    className={compact ? (isDark ? 'ml-1' : 'text-gray-500 ml-1') : isDark ? 'ml-3' : 'text-gray-500 ml-3'}
                    style={{
                        fontSize: '0.5em',
                        color: isDark && isDigital7 ? '#ffffff' : isDark ? '#ffffff' : undefined,
                    }}
                >
                    {renderDigits(currentSecond, '0.5em')}
                </span>
            </span>
            {isCountdown && (
                <div
                    className={isDark ? '' : 'text-gray-500'}
                    style={{ color: isDark ? '#ffffff' : undefined }}
                ></div>
            )}
        </div>
    );
};
